import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { SEARCH_PRODUCTS, SEARCH_RECIPES } from '../lib/shopify-queries';
import type { Product } from '../components/ProductCard';
import { convertShopifyProduct } from './useShopifyProducts';
import { useLanguage } from './useLanguage';
import { useMarket } from './useMarketZustand';
import { logger } from '../utils/logger';

// Recipe interface for search results
export interface Recipe {
  id: string;
  title: string;
  handle: string;
  excerpt: string;
  content: string;
  contentHtml: string;
  image?: {
    url: string;
    altText?: string;
  };
  publishedAt: string;
  author?: {
    firstName: string;
    lastName: string;
  };
  tags: string[];
}

export interface SearchResults {
  products: Product[];
  recipes: Recipe[];
  totalResults: number;
  isLoading: boolean;
  error: Error | null;
}

export function useShopifySearch() {
  const { currentLanguage, isInitialized } = useLanguage();
  const { currentMarket } = useMarket();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults>({
    products: [],
    recipes: [],
    totalResults: 0,
    isLoading: false,
    error: null,
  });

  // Helper functions for Shopify API parameters
  const getShopifyLanguageCode = (lang: string): string => {
    const langMap: Record<string, string> = {
      en: 'EN',
      de: 'DE',
      pl: 'PL',
      uk: 'UK',
    };
    return langMap[lang] || 'EN';
  };

  const getShopifyCountryCode = (market: string): string => {
    const countryMap: Record<string, string> = {
      de: 'DE',
      pl: 'PL',
    };
    return countryMap[market] || 'DE';
  };

  // Search products query
  const searchVariables = { 
    query: searchQuery, 
    first: 250,
    language: getShopifyLanguageCode(currentLanguage),
    country: getShopifyCountryCode(currentMarket)
  };
  
  
  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useQuery(SEARCH_PRODUCTS, {
    variables: searchVariables,
    skip: !searchQuery.trim() || !isInitialized,
    errorPolicy: 'all',
  });

  // Search recipes query
  const {
    data: recipesData,
    loading: recipesLoading,
    error: recipesError,
    refetch: refetchRecipes,
  } = useQuery(SEARCH_RECIPES, {
    variables: { query: searchQuery, first: 250 },
    skip: !searchQuery.trim(),
    errorPolicy: 'all',
  });

  // Calculate relevance score for search results
  const calculateRelevance = (product: any, searchQuery: string): number => {
    const query = searchQuery.toLowerCase().trim();
    const title = product.title?.toLowerCase() || '';
    const description = product.description?.toLowerCase() || '';
    const tags = product.tags?.join(' ').toLowerCase() || '';

    let score = 0;

    // Exact match in title gets highest score
    if (title === query) score += 100;
    // Title starts with query
    else if (title.startsWith(query)) score += 80;
    // Title contains query as whole word
    else if (
      title.includes(` ${query} `) ||
      title.includes(`${query} `) ||
      title.includes(` ${query}`)
    )
      score += 60;
    // Title contains query as substring
    else if (title.includes(query)) score += 40;

    // Description matches (lower priority)
    if (description.includes(query)) score += 20;

    // Tags match (medium priority)
    if (tags.includes(query)) score += 30;

    return score;
  };

  // Transform Shopify product data to our Product interface using convertShopifyProduct
  const transformProducts = (data: any, searchQuery?: string): Product[] => {
    if (!data?.products?.edges) return [];


    const products = data.products.edges.map((edge: any) => {
      const shopifyProduct = edge.node;
      
      // Use the shared convertShopifyProduct function
      const product = convertShopifyProduct(shopifyProduct);
      
      // Add relevance score for search results
      if (searchQuery) {
        (product as any).relevanceScore = calculateRelevance(shopifyProduct, searchQuery);
      }
      
      return product;
    });

    // Sort by relevance score (highest first) when search query exists
    if (searchQuery) {
      products.sort((a: any, b: any) => (b as any).relevanceScore - (a as any).relevanceScore);
    }

    return products;
  };

  // Transform Shopify recipes data to our Recipe interface
  const transformRecipes = (data: any): Recipe[] => {
    if (!data?.blogs?.edges) return [];

    const recipes: Recipe[] = [];
    data.blogs.edges.forEach((blog: any) => {
      if (blog.node.articles?.edges) {
        blog.node.articles.edges.forEach((article: any) => {
          const node = article.node;
          recipes.push({
            id: node.id,
            title: node.title,
            handle: node.handle,
            excerpt: node.excerpt || '',
            content: node.content || '',
            contentHtml: node.contentHtml || '',
            image: node.image
              ? {
                  url: node.image.url,
                  altText: node.image.altText,
                }
              : undefined,
            publishedAt: node.publishedAt,
            author: node.author
              ? {
                  firstName: node.author.firstName || '',
                  lastName: node.author.lastName || '',
                }
              : undefined,
            tags: node.tags || [],
          });
        });
      }
    });

    return recipes;
  };

  // Perform search
  const search = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults({
          products: [],
          recipes: [],
          totalResults: 0,
          isLoading: false,
          error: null,
        });
        return;
      }

      setSearchQuery(query);
      setSearchResults(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        // Execute both searches in parallel
        const [productsResult, recipesResult] = await Promise.all([
          refetchProducts({ query, first: 250 }),
          refetchRecipes({ query, first: 250 }),
        ]);

        const products = transformProducts(productsResult.data, query);
        const recipes = transformRecipes(recipesResult.data);

        setSearchResults({
          products,
          recipes,
          totalResults: products.length + recipes.length,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults(prev => ({
          ...prev,
          isLoading: false,
          error: error as Error,
        }));
      }
    },
    [refetchProducts, refetchRecipes]
  );

  // Clear search results
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults({
      products: [],
      recipes: [],
      totalResults: 0,
      isLoading: false,
      error: null,
    });
  }, []);

  return {
    search,
    clearSearch,
    searchQuery,
    searchResults: {
      ...searchResults,
      isLoading: productsLoading || recipesLoading,
      error: productsError || recipesError || searchResults.error,
    },
  };
}
